import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { ACCOUNTING_QUERY_KEYS } from "../../configs/queryKeys";
import { accountingService } from "../../services/accounting.service";
import { extractApiErrorMessage } from "./utils";
import type { LedgerAllocation } from "../../types/accounting.types";

type UseMoveItemsOptions = {
  allocations: LedgerAllocation[]
  bankMovementId: string
};

/**
 * Le déplacement ne modifie que le mouvement de rattachement, mais les
 * validations de l'API tirent sur toute la ligne : une écriture dont la date
 * stockée sort de la fenêtre acceptée est refusée sur un champ que cet écran
 * n'affiche pas. Sans la raison ni la ligne fautive, le comptable n'a aucun
 * moyen de savoir laquelle reprendre.
 */
const describeMoveFailure = (error: unknown, allocation: LedgerAllocation | null): string => {
  const reason = extractApiErrorMessage(error);
  const label = allocation?.description?.trim() || allocation?.date;
  return label ? `« ${label} » : ${reason}` : reason;
};

export const useMoveItems = ({ allocations, bankMovementId }: UseMoveItemsOptions) => {
  const queryClient = useQueryClient();

  // Selection mode
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Allocation selection
  const [selectedAllocationIds, setSelectedAllocationIds] = useState<Set<string>>(new Set());

  // Settlement selection
  const [selectedSettlementIds, setSelectedSettlementIds] = useState<Set<string>>(new Set());

  // Dialog state
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [moveDialogType, setMoveDialogType] = useState<"allocations" | "settlements">("allocations");

  // Moving state
  const [isMoving, setIsMoving] = useState(false);

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode((prev) => {
      if (prev) {
        // Exiting selection mode: clear selections
        setSelectedAllocationIds(new Set());
        setSelectedSettlementIds(new Set());
      }
      return !prev;
    });
  }, []);

  const toggleAllocationSelect = useCallback((id: string) => {
    setSelectedAllocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllAllocationsSelect = useCallback(() => {
    const selectableAllocations = allocations.filter((a) => !a.locked && !a.settlementId);
    setSelectedAllocationIds((prev) => {
      if (prev.size === selectableAllocations.length) return new Set();
      return new Set(selectableAllocations.map((a) => a.id));
    });
  }, [allocations]);

  const toggleSettlementSelect = useCallback((id: string) => {
    setSelectedSettlementIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearAllSelections = useCallback(() => {
    setSelectedAllocationIds(new Set());
    setSelectedSettlementIds(new Set());
  }, []);

  const openMoveDialog = useCallback((type: "allocations" | "settlements") => {
    setMoveDialogType(type);
    setIsMoveDialogOpen(true);
  }, []);

  const closeMoveDialog = useCallback((open?: boolean) => {
    setIsMoveDialogOpen(open ?? false);
  }, []);

  const moveAllocations = useCallback(async (targetBankMovementId: string) => {
    const ids = Array.from(selectedAllocationIds);
    if (ids.length === 0) return;

    setIsMoving(true);
    let movedCount = 0;
    let refusedAllocation: LedgerAllocation | null = null;

    try {
      for (const allocationId of ids) {
        const allocation = allocations.find((a) => a.id === allocationId);
        if (!allocation) continue;

        refusedAllocation = allocation;
        await accountingService.moveLedgerAllocation(allocationId, targetBankMovementId);
        refusedAllocation = null;
        movedCount++;
      }

      toast.success(`${movedCount} écriture(s) déplacée(s) avec succès`);

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });

      // Clean up
      setSelectedAllocationIds(new Set());
      setIsMoveDialogOpen(false);
      setIsSelectionMode(false);
    }
    catch (error) {
      const description = describeMoveFailure(error, refusedAllocation);
      if (movedCount > 0) {
        toast.warning(
          `${movedCount} sur ${ids.length} écriture(s) déplacée(s). Certaines ont échoué.`,
          { description },
        );
        await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
        await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      }
      else {
        toast.error("Erreur lors du déplacement des écritures", { description });
      }
    }
    finally {
      setIsMoving(false);
    }
  }, [selectedAllocationIds, allocations, queryClient]);

  const moveSettlements = useCallback(async (targetBankMovementId: string) => {
    const ids = Array.from(selectedSettlementIds);
    if (ids.length === 0) return;

    setIsMoving(true);
    let movedCount = 0;

    try {
      for (const settlementId of ids) {
        await accountingService.updateSettlement(settlementId, { bankMovementId: targetBankMovementId });
        movedCount++;
      }

      toast.success(`${movedCount} règlement(s) déplacé(s) avec succès`);

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
      await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
      // Settlement changes can affect the status of linked entities
      queryClient.invalidateQueries({ queryKey: ["refund-requests"] });
      queryClient.invalidateQueries({ queryKey: ["ecom-orders"] });

      // Clean up
      setSelectedSettlementIds(new Set());
      setIsMoveDialogOpen(false);
      setIsSelectionMode(false);
    }
    catch (error) {
      const description = extractApiErrorMessage(error);
      if (movedCount > 0) {
        toast.warning(
          `${movedCount} sur ${ids.length} règlement(s) déplacé(s). Certains ont échoué.`,
          { description },
        );
        await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.ledgerAllocations });
        await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.bankMovements });
        await queryClient.invalidateQueries({ queryKey: ACCOUNTING_QUERY_KEYS.settlements });
        queryClient.invalidateQueries({ queryKey: ["refund-requests"] });
        queryClient.invalidateQueries({ queryKey: ["ecom-orders"] });
      }
      else {
        toast.error("Erreur lors du déplacement des règlements", { description });
      }
    }
    finally {
      setIsMoving(false);
    }
  }, [selectedSettlementIds, queryClient]);

  const handleConfirmMove = useCallback(async (targetBankMovementId: string) => {
    if (moveDialogType === "allocations") {
      await moveAllocations(targetBankMovementId);
    }
    else {
      await moveSettlements(targetBankMovementId);
    }
  }, [moveDialogType, moveAllocations, moveSettlements]);

  // Computed values
  const selectedAllocations = allocations.filter((a) => selectedAllocationIds.has(a.id));
  const selectedAllocationsDebit = selectedAllocations.reduce((sum, a) => sum + (a.debit ?? 0), 0);
  const selectedAllocationsCredit = selectedAllocations.reduce((sum, a) => sum + (a.credit ?? 0), 0);

  return {
    // Selection mode
    isSelectionMode,
    toggleSelectionMode,

    // Allocation selection
    selectedAllocationIds,
    toggleAllocationSelect,
    toggleAllAllocationsSelect,
    selectedAllocationsDebit,
    selectedAllocationsCredit,

    // Settlement selection
    selectedSettlementIds,
    toggleSettlementSelect,

    // Common
    clearAllSelections,

    // Dialog
    isMoveDialogOpen,
    moveDialogType,
    openMoveDialog,
    closeMoveDialog,
    handleConfirmMove,
    isMoving,

    // Source movement info
    sourceBankMovementId: bankMovementId,
  };
};
